import { mapsUrl } from "@/lib/maps";
import { openInstall, isStandaloneApp } from "@/components/InstallPrompt";
import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Calendar, Instagram, MapPin, Mail, Download } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import InstallPrompt from "@/components/InstallPrompt";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/my-bookings", label: "My Bookings" },
];

export default function SiteLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="grain min-h-screen bg-[#050505] relative">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "glass" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
            <img src="/xmd-logo.png" alt="XMD Productions" className="h-9 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-mono-label uppercase text-sm tracking-[0.15em] transition-colors ${
                  loc.pathname === n.to ? "text-[#D4AF37]" : "text-white/70 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
            <button
              onClick={() => navigate("/book")}
              data-testid="header-book-btn"
              className="btn-gold rounded-full px-6 py-2 text-sm uppercase tracking-wide font-mono-label"
            >
              Book Now
            </button>
          </nav>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden glass overflow-hidden border-t border-white/10"
              data-testid="mobile-menu"
            >
              <div className="px-5 py-4 flex flex-col gap-1">
                {NAV.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`py-3 font-mono-label uppercase tracking-[0.15em] text-sm border-b border-white/5 ${
                      loc.pathname === n.to ? "text-[#D4AF37]" : "text-white/80"
                    }`}
                  >
                    {n.label}
                  </Link>
                ))}
                <button
                  onClick={() => navigate("/book")}
                  data-testid="mobile-book-btn"
                  className="btn-gold rounded-full px-6 py-3 mt-4 text-sm uppercase tracking-wide font-mono-label"
                >
                  Book Now
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>

      <Footer />
      <InstallPrompt />
    </div>
  );
}

function Footer() {
  return (
    <footer className="relative z-10 border-t border-white/10 bg-[#0a0a0a] pt-16 pb-28 md:pb-10" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-5">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <img src="/xmd-logo.png" alt="XMD Productions" className="h-12 w-auto mb-4" />
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              South Florida's premium media production studio. Photography, video, podcast, livestream & more.
            </p>
          </div>
          <div>
            <h4 className="font-mono-label uppercase tracking-[0.2em] text-xs text-[#D4AF37] mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-3"><Phone size={16} className="text-[#D4AF37]" /> <a href="tel:+15615037743" className="hover:text-[#D4AF37]" data-testid="footer-phone-1">+1 561-503-7743</a></li>
              <li className="flex items-center gap-3"><Phone size={16} className="text-[#D4AF37]" /> <a href="tel:+15618912557" className="hover:text-[#D4AF37]" data-testid="footer-phone-2">+1 561-891-2557</a></li>
              <li className="flex items-center gap-3"><Mail size={16} className="text-[#D4AF37]" /> info@xmdproductions.com</li>
              <li className="flex items-start gap-3"><MapPin size={16} className="text-[#D4AF37] mt-0.5 shrink-0" /> <a href={mapsUrl()} target="_blank" rel="noreferrer" className="hover:text-[#D4AF37]" data-testid="footer-address">3020 High Ridge Rd, Suite 600A, Boynton Beach, FL 33426</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-mono-label uppercase tracking-[0.2em] text-xs text-[#D4AF37] mb-4">Explore</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link to="/services" className="hover:text-[#D4AF37]">Services</Link></li>
              <li><Link to="/portfolio" className="hover:text-[#D4AF37]">Portfolio</Link></li>
              <li><Link to="/book" className="hover:text-[#D4AF37]">Book a Session</Link></li>
              {!isStandaloneApp() && (
                <li><button type="button" onClick={openInstall} data-testid="footer-install-app" className="hover:text-[#D4AF37] flex items-center gap-2"><Download size={14} className="text-[#D4AF37]" /> Install App</button></li>
              )}
              <li><Link to="/admin/login" className="hover:text-[#D4AF37]">Staff Login</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs">© {new Date().getFullYear()} XMD Productions. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-white/50 hover:text-[#D4AF37] text-xs">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-white/50 hover:text-[#D4AF37] text-xs">Terms &amp; Conditions</Link>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noreferrer"
              className="text-white/50 hover:text-[#D4AF37] flex items-center gap-2 text-xs"
            >
              <Instagram size={16} /> @xmdproductions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
